import { TypedLocale } from 'payload';

import { customTranslations } from '@/custom-translations';

// Utility type to support nested translation keys
type NestedKeyOf<T, Prefix extends string = ''> = {
  [K in keyof T]: T[K] extends object
    ? `${Prefix}${K & string}` | NestedKeyOf<T[K], `${Prefix}${K & string}.`>
    : `${Prefix}${K & string}`;
}[keyof T];

// Extract all valid translation keys
export type TranslationKeys = NestedKeyOf<typeof customTranslations.en>;

/**
 * Returns a dictionary function for translating keys.
 * Supports deeply nested keys (e.g., "loadAllButton.showAll").
 */
export function getDictionary(locale: TypedLocale) {
  const translations = customTranslations[locale] ?? customTranslations.en;

  return (key: TranslationKeys): string => {
    // Split nested keys like "loadAllButton.showAll"
    const keys = key.split('.') as string[];

    // Traverse the object to get the translation
    let result: any = translations;
    for (const k of keys) {
      if (result[k] === undefined) return key; // Fallback to returning the key
      result = result[k];
    }

    return typeof result === 'string' ? result : key; // Ensure it always returns a string
  };
}

/**
 * Server-side translation function for use in SSR.
 */
export function getServerTranslations(locale: TypedLocale) {
  return getDictionary(locale);
}
